import { CloseCircleFilled } from '@ant-design/icons';
import type { BaseType } from 'antd/lib/typography/Base';
import type { FieldProps } from 'formik';
import { type CSSProperties, type ComponentType, type JSX, useMemo } from 'react';

import './withFieldError.scss';

import type { InputStatus } from 'antd/lib/_util/statusUtils';
import { ValidationError } from 'src/components/base';
import { cNs } from 'src/helpers/cNs';

const componentStatusByErrorType: Readonly<Record<BaseType, InputStatus>> = Object.freeze({
  danger: 'error',
  secondary: 'warning',
  warning: 'warning',
  success: '',
});

export type IWithFieldErrorProps = {
  isShowErrorMessage?: boolean;

  touchOnMount?: boolean;

  style?: CSSProperties;
  prefix?: JSX.Element | string;
  className?: string;
  errorType?: BaseType;
};

export function withFieldError<T extends FieldProps>(Component: ComponentType<T>) {
  const useWrapped = (props: IWithFieldErrorProps & T) => {
    const {
      isShowErrorMessage = true,
      errorType = 'danger',

      ...componentProps
    } = props;

    const {
      style,
      field,
      form: { getFieldMeta },
      prefix,
      className,
    } = componentProps;

    const hasError = useMemo(() => {
      const fieldMeta = getFieldMeta(field.name);

      return fieldMeta.touched ? !!fieldMeta.error : !!fieldMeta.initialError;
    }, [getFieldMeta, field.name]);

    const statusPatch = useMemo(
      // Remain ability to `override status from outside`. Omit `status` property for component if status is undefined here.
      // Otherwise undefined value overrides value from `componentProps`
      () => (hasError ? { status: componentStatusByErrorType[errorType], danger: true } : {}),
      [hasError, errorType]
    );

    return (
      <div className={cNs('with-field-error__container', className)} style={style}>
        <Component
          {...(componentProps as Omit<IWithFieldErrorProps, 'isShowErrorMessage'> & T)}
          {...statusPatch}
          prefix={hasError ? <CloseCircleFilled style={{ alignSelf: 'center' }} /> : prefix}
        />
        {isShowErrorMessage && <ValidationError name={field.name} errorType={errorType} />}
      </div>
    );
  };

  useWrapped.displayName = `withFieldError(${Component.displayName || Component.name || 'Component'})`;

  return useWrapped;
}
